import {useState} from 'react';
import {Link, useNavigate} from 'react-router-dom';
import {Button} from '@common-web/components/ui/button';
import {Tabs, TabsContent, TabsList, TabsTrigger} from '@common-web/components/ui/tabs';
import {useAuthStore} from '@common-web/stores/authStore';
import {PageHeader} from '@common-web/components/page-header';
import {UserPlus, Shield, Building2, UsersIcon, KeyRound, ArrowLeft} from 'lucide-react';
import {RegistrationSuccess} from './components/RegistrationSuccess';
import {InternalUserForm} from './components/InternalUserForm';
import {ClientUserForm} from './components/ClientUserForm';
import {GroupUserForm} from './components/GroupUserForm';
import {ExternalUserForm} from './components/ExternalUserForm';

export type CreateRegistrationResult = {
  id: string;
  email: string;
  code: string;
  expiresAt: string | Date;
};

export const INVITE_EXPIRATION_DAY_OPTIONS = [1, 3, 7, 14, 30];

export function InternalCreateUser() {
  const isInternalUser = useAuthStore((state) => state.isInternalUser());
  const navigate = useNavigate();
  // For client users, default to 'client' tab since they can't create internal users
  const [activeTab, setActiveTab] = useState<'internal' | 'client' | 'group' | 'external'>(
    isInternalUser ? 'internal' : 'client',
  );
  const [showSuccess, setShowSuccess] = useState(false);
  const [registrationData, setRegistrationData] = useState<CreateRegistrationResult | null>(null);

  const handleSuccess = (data: CreateRegistrationResult) => {
    setRegistrationData(data);
    setShowSuccess(true);
  };

  const handleReset = () => {
    setShowSuccess(false);
    setRegistrationData(null);
  };

  if (showSuccess && registrationData) {
    return (
      <RegistrationSuccess
        registrationData={registrationData}
        onReset={handleReset}
        onBack={() => navigate('/users')}
      />
    );
  }

  return (
    <div className='min-h-screen bg-gradient-to-br from-gray-50 via-white to-brand-blue/5'>
      <div className='flex flex-col gap-0'>
        <PageHeader
          title='Create New User'
          description='Generate registration codes for new internal users, client admins, or group users, or issue an API token for an external integration'
          icon={<UserPlus className='h-6 w-6' />}
        />
        <div className='px-8 py-6 max-w-4xl mx-auto w-full'>
          <Button
            asChild
            variant='ghost'
            className='mb-4 -ml-2 text-brand-gray hover:text-brand-navy'
          >
            <Link to={`/users`}>
              <ArrowLeft className='h-4 w-4 mr-2' />
              Back to Users
            </Link>
          </Button>
          <Tabs
            value={activeTab}
            onValueChange={(value) =>
              setActiveTab(value as 'internal' | 'client' | 'group' | 'external')
            }
          >
            <TabsList
              className={`bg-gray-100 p-1 w-full grid ${isInternalUser ? 'grid-cols-4' : 'grid-cols-2'}`}
            >
              {isInternalUser && (
                <TabsTrigger
                  value='internal'
                  className='data-[state=active]:bg-white data-[state=active]:text-brand-navy data-[state=active]:shadow-sm'
                >
                  <Shield className='h-4 w-4 mr-2' />
                  Internal User
                </TabsTrigger>
              )}
              <TabsTrigger
                value='client'
                className='data-[state=active]:bg-white data-[state=active]:text-brand-navy data-[state=active]:shadow-sm'
              >
                <Building2 className='h-4 w-4 mr-2' />
                Client Admin
              </TabsTrigger>
              <TabsTrigger
                value='group'
                className='data-[state=active]:bg-white data-[state=active]:text-brand-navy data-[state=active]:shadow-sm'
              >
                <UsersIcon className='h-4 w-4 mr-2' />
                Group User
              </TabsTrigger>
              {isInternalUser && (
                <TabsTrigger
                  value='external'
                  className='data-[state=active]:bg-white data-[state=active]:text-brand-navy data-[state=active]:shadow-sm'
                >
                  <KeyRound className='h-4 w-4 mr-2' />
                  External API
                </TabsTrigger>
              )}
            </TabsList>
            <TabsContent value='internal' className='mt-6'>
              <InternalUserForm onSuccess={handleSuccess} />
            </TabsContent>
            <TabsContent value='client' className='mt-6'>
              <ClientUserForm onSuccess={handleSuccess} />
            </TabsContent>
            <TabsContent value='group' className='mt-6'>
              <GroupUserForm onSuccess={handleSuccess} />
            </TabsContent>
            {isInternalUser && (
              <TabsContent value='external' className='mt-6'>
                <ExternalUserForm />
              </TabsContent>
            )}
          </Tabs>
        </div>
      </div>
    </div>
  );
}
